interface CacheEntity<PromiseReturn> {
  value: PromiseReturn;
  createdAt: number;
  dependency: unknown;
}

type Resolver<ResolverParams, ResolverReturn> = (params: {
  params: ResolverParams;
  memoize: Memoize;
}) => Promise<ResolverReturn>;

export type Memoize = <MemoCallback extends (...args: any) => Promise<any>>(
  callback: MemoCallback,
  cacheKey: (...params: Parameters<MemoCallback>) => string,
) => (...args: Parameters<MemoCallback>) => Promise<Awaited<ReturnType<MemoCallback>>>;

export class CacheWithTTL<ResolverParams, ResolverReturn> {
  private _ttl: number;
  private _resolver: Resolver<ResolverParams, ResolverReturn>;
  private _cache: Map<string, CacheEntity<unknown>> = new Map();
  private _cleanupHandle?: number = undefined;

  constructor({
    ttl,
    resolver,
  }: {
    ttl: number;
    resolver: Resolver<ResolverParams, ResolverReturn>;
  }) {
    this._ttl = ttl;
    this._resolver = resolver;
  }

  private _cleanup() {
    const now = Date.now();

    for (const [key, value] of this._cache.entries()) {
      if (value.createdAt + this._ttl < now) {
        this._cache.delete(key);
      }
    }

    this._cleanupHandle = undefined;
  }

  private memoize: Memoize = (callback, cacheKey) => {
    return async (...args) => {
      if (this._cleanupHandle === undefined) {
        this._cleanupHandle = window.requestIdleCallback(this._cleanup.bind(this));
      }

      const key = cacheKey(...args);

      const cached = this._cache.get(key);

      if (cached && cached.createdAt + this._ttl > Date.now()) {
        return cached.value;
      }

      const value = await callback(...args);
      this._cache.set(key, { value, createdAt: Date.now(), dependency: args });
      return value;
    };
  };

  resolve(params: ResolverParams) {
    return this._resolver({
      params,
      memoize: this.memoize.bind(this),
    });
  }

  static resolve<T, R>(resolver: Resolver<T, R>, ttl: number) {
    const cacheInstance = new CacheWithTTL({ ttl, resolver });
    return cacheInstance.resolve.bind(cacheInstance);
  }
}
